import { useState } from "react";
import { ApiError } from "../lib/api.ts";
import { proposeAndApply } from "../lib/changes.ts";
import { navigate, onLink } from "../lib/router.ts";
import { useGet, useSession } from "../lib/session.tsx";
import type { CollectionDef, RoleDef, RoleRule } from "../lib/types.ts";
import { ruleText } from "./changesets.tsx";

type Scope = RoleRule["read"];
type FieldMode = "edit" | "readOnly" | "hidden";

const SCOPES: { value: Scope; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "own", label: "Só os próprios" },
  { value: "none", label: "Nenhum" },
];

/** Custom roles of the store. Changing them is a changeset (risk "sensível"), like any structure. */
export function Roles() {
  const s = useSession();
  const { data, error, reload } = useGet<{ roles: RoleDef[] }>("/v1/roles");
  const [editing, setEditing] = useState<RoleDef | "new" | null>(null);

  if (editing) {
    return (
      <RoleEditor
        role={editing === "new" ? null : editing}
        collections={s.collections}
        taken={(data?.roles ?? []).map((r) => r.name)}
        onDone={() => {
          setEditing(null);
          reload();
        }}
      />
    );
  }
  return (
    <div className="stack" style={{ maxWidth: 820 }}>
      <div className="topbar">
        <h1>Papéis</h1>
        <span className="spacer" />
        {s.canBuild && (
          <button className="btn primary" onClick={() => setEditing("new")}>
            Novo papel
          </button>
        )}
      </div>
      <div className="card stack">
        <p style={{ margin: 0 }}>
          <strong>Dono</strong>, <strong>Construtor</strong> e <strong>Operador</strong> veem e alteram todos os dados. Crie papéis próprios para quem deve ver só uma parte: por coleção, quais registros (todos ou só os que a pessoa criou), o que pode fazer e quais campos ficam ocultos ou só para leitura.
        </p>
        <p className="muted" style={{ margin: 0 }}>
          As mesmas regras valem no app e para a IA da pessoa. Quem tem papéis é definido em{" "}
          {s.isOwner ? (
            <a href="/membros" onClick={onLink}>
              Membros
            </a>
          ) : (
            "Membros (pelo dono)"
          )}
          .
        </p>
      </div>
      {error && <p className="error">{error.message}</p>}
      <div className="card list">
        <strong>Papéis desta loja</strong>
        {data?.roles.map((r) => (
          <div key={r.name} className="list-item" style={{ alignItems: "flex-start" }}>
            <div className="stack" style={{ gap: 2 }}>
              <span style={{ fontWeight: 600 }}>
                {r.label} <code className="muted">{r.name}</code>
              </span>
              {r.description && <span className="muted">{r.description}</span>}
              {r.rules.map((x) => (
                <span key={x.collection} style={{ fontSize: 12.5 }}>
                  {ruleText(x)}
                </span>
              ))}
              {!r.rules.length && <span className="muted" style={{ fontSize: 12.5 }}>Sem regras: não vê nenhuma coleção.</span>}
            </div>
            <span className="spacer" />
            {s.canBuild && (
              <button className="btn sm" onClick={() => setEditing(r)}>
                Editar
              </button>
            )}
          </div>
        ))}
        {data && !data.roles.length && <p className="muted" style={{ margin: 0 }}>Nenhum papel próprio ainda.</p>}
      </div>
    </div>
  );
}

function RoleEditor({ role, collections, taken, onDone }: { role: RoleDef | null; collections: CollectionDef[]; taken: string[]; onDone: () => void }) {
  const s = useSession();
  const [name, setName] = useState(role?.name ?? "");
  const [label, setLabel] = useState(role?.label ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [rules, setRules] = useState<Record<string, RoleRule>>(() => Object.fromEntries((role?.rules ?? []).map((r) => [r.collection, r])));
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const setRule = (c: string, patch: Partial<RoleRule> | null) =>
    setRules((all) => {
      const next = { ...all };
      if (patch === null) delete next[c];
      else next[c] = { collection: c, read: "all", create: false, update: "none", delete: "none", ...all[c], ...patch };
      return next;
    });

  async function save() {
    setBusy(true);
    setErr(null);
    const list = collections.map((c) => rules[c.name]).filter((r): r is RoleRule => !!r);
    const ops = role
      ? [
          {
            op: "role.update",
            role: role.name,
            set: { ...(name !== role.name ? { name } : {}), label: label || undefined, description: description.trim() || null, rules: list },
          },
        ]
      : [{ op: "role.create", name, ...(label ? { label } : {}), ...(description.trim() ? { description: description.trim() } : {}), rules: list }];
    try {
      const { changeset, applied } = await proposeAndApply(`${role ? "Alterar" : "Criar"} papel ${name}`, ops);
      if (applied) {
        s.toast("Papel salvo.");
        onDone();
      } else navigate(`/changesets/${changeset.id}`);
    } catch (e) {
      setErr(e instanceof ApiError ? [e.message, ...e.details.map((d) => `${d.path.join(".")}: ${d.message}`)].join(" ") : "Não foi possível salvar.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!role || !confirm(`Remover o papel ${role.label}? Quem só tinha este papel deixa de ver os dados.`)) return;
    setBusy(true);
    try {
      await proposeAndApply(`Remover papel ${role.name}`, [{ op: "role.remove", role: role.name }]);
      s.toast("Papel removido.");
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro");
      setBusy(false);
    }
  }

  const nameOk = /^[a-z][a-z0-9_]{0,62}$/.test(name) && !["dono", "construtor", "operador"].includes(name) && (name === role?.name || !taken.includes(name));

  return (
    <div className="stack" style={{ maxWidth: 900 }}>
      <div className="topbar">
        <button className="linkish" onClick={onDone}>
          ← Papéis
        </button>
      </div>
      <div className="topbar">
        <h1>{role ? `Papel ${role.label}` : "Novo papel"}</h1>
        <span className="spacer" />
        {role && (
          <button className="btn danger" onClick={remove} disabled={busy}>
            Remover
          </button>
        )}
        <button className="btn primary" onClick={save} disabled={busy || !nameOk}>
          Salvar
        </button>
      </div>
      {err && <p className="error">{err}</p>}
      <div className="card form">
        <label className={`fld ${name && !nameOk ? "bad" : ""}`}>
          Nome (identificador)
          <input value={name} onChange={(e) => setName(e.target.value.toLowerCase())} placeholder="vendedor" />
          {name && !nameOk && <span className="err">Letras minúsculas, números e _, começando por letra; sem repetir um papel existente.</span>}
        </label>
        <label className="fld">
          Rótulo
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Vendedor" />
        </label>
        <label className="fld wide">
          Descrição
          <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Vende e acompanha os próprios pedidos" />
        </label>
      </div>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Acesso por coleção</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Coleção sem acesso fica invisível para quem tem só este papel. Se a pessoa tiver vários papéis, vale o que qualquer um permitir.
        </p>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Coleção</th>
                <th>Vê</th>
                <th>Cria</th>
                <th>Edita</th>
                <th>Apaga</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {collections.map((c) => {
                const r = rules[c.name];
                const scope = (k: "read" | "update" | "delete") => (
                  <select value={r?.[k] ?? "none"} disabled={!r} onChange={(e) => setRule(c.name, { [k]: e.target.value as Scope })} aria-label={`${k} ${c.label}`}>
                    {SCOPES.map((x) => (
                      <option key={x.value} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                );
                return (
                  <RuleRows
                    key={c.name}
                    c={c}
                    rule={r}
                    open={open === c.name}
                    onToggleOpen={() => setOpen(open === c.name ? null : c.name)}
                    onEnable={(on) => setRule(c.name, on ? {} : null)}
                    onChange={(p) => setRule(c.name, p)}
                    scope={scope}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
        {!collections.length && <p className="muted">A loja ainda não tem coleções.</p>}
      </div>
    </div>
  );
}

function RuleRows({
  c,
  rule,
  open,
  onToggleOpen,
  onEnable,
  onChange,
  scope,
}: {
  c: CollectionDef;
  rule: RoleRule | undefined;
  open: boolean;
  onToggleOpen: () => void;
  onEnable: (on: boolean) => void;
  onChange: (p: Partial<RoleRule>) => void;
  scope: (k: "read" | "update" | "delete") => React.ReactNode;
}) {
  const mode = (f: string): FieldMode => (rule?.hidden?.includes(f) ? "hidden" : rule?.readOnly?.includes(f) ? "readOnly" : "edit");
  const setMode = (f: string, m: FieldMode) => {
    const hidden = (rule?.hidden ?? []).filter((x) => x !== f);
    const readOnly = (rule?.readOnly ?? []).filter((x) => x !== f);
    if (m === "hidden") hidden.push(f);
    if (m === "readOnly") readOnly.push(f);
    onChange({ hidden: hidden.length ? hidden : undefined, readOnly: readOnly.length ? readOnly : undefined });
  };
  const restricted = (rule?.hidden?.length ?? 0) + (rule?.readOnly?.length ?? 0) + (rule?.filter ? 1 : 0);
  return (
    <>
      <tr>
        <td>
          <label className="checkline">
            <input type="checkbox" checked={!!rule} onChange={(e) => onEnable(e.target.checked)} /> {c.label}
          </label>
        </td>
        <td>{scope("read")}</td>
        <td>
          <input type="checkbox" disabled={!rule} checked={!!rule?.create} onChange={(e) => onChange({ create: e.target.checked })} aria-label={`criar ${c.label}`} />
        </td>
        <td>{scope("update")}</td>
        <td>{scope("delete")}</td>
        <td>
          {rule && (
            <button className="linkish" onClick={onToggleOpen}>
              {open ? "fechar" : `campos e filtro${restricted ? ` (${restricted})` : ""}`}
            </button>
          )}
        </td>
      </tr>
      {rule && open && (
        <tr>
          <td colSpan={6} style={{ whiteSpace: "normal", maxWidth: "none" }}>
            <div className="stack">
              <div className="form">
                {c.fields.map((f) => (
                  <label key={f.name} className="fld">
                    {f.label}
                    <select value={mode(f.name)} onChange={(e) => setMode(f.name, e.target.value as FieldMode)}>
                      <option value="edit">Vê e edita</option>
                      <option value="readOnly">Só leitura</option>
                      <option value="hidden">Oculto</option>
                    </select>
                  </label>
                ))}
              </div>
              <RuleFilter value={rule.filter} onChange={(filter) => onChange({ filter })} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function RuleFilter({ value, onChange }: { value: Record<string, unknown> | undefined; onChange: (v: Record<string, unknown> | undefined) => void }) {
  const [text, setText] = useState(value ? JSON.stringify(value) : "");
  const [bad, setBad] = useState(false);
  return (
    <label className={`fld ${bad ? "bad" : ""}`}>
      Só os registros que casam com este filtro (JSON, opcional)
      <input
        style={{ fontFamily: "var(--f-mono)", fontSize: 12 }}
        placeholder='{"loja": "centro"}'
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (!e.target.value.trim()) {
            setBad(false);
            return onChange(undefined);
          }
          try {
            const v = JSON.parse(e.target.value) as unknown;
            const ok = !!v && typeof v === "object" && !Array.isArray(v);
            setBad(!ok);
            if (ok) onChange(v as Record<string, unknown>);
          } catch {
            setBad(true);
          }
        }}
      />
      {bad && <span className="err">JSON inválido</span>}
    </label>
  );
}
